import { PmmProps, RefundPresign } from '@optimex-xyz/trade-wallet-sdk'

import { IToken } from '@/types/token.type'

import { TradePairInfo } from './across'
import { AffiliateStruct } from './aff'
import { Pagination } from './common'
import { InputDataPresubmitTrade } from './sendTrade.type'

export enum TradeStatus {
  // Initial states
  INIT = 'INIT',
  PRE_SUBMIT = 'PRE_SUBMIT',

  SUBMITTED_TO_BE = 'SUBMITTED_TO_BE', // Bridge only

  // Deposit flow
  DEPOSITED = 'DEPOSITED',
  SUBMITTED_TO_SOLVER = 'SUBMITTED_TO_SOLVER',

  // Main flow
  SUBMITTED = 'SUBMITTED',
  DEPOSIT_CONFIRMED = 'DEPOSIT_CONFIRMED',
  PMM_SELECTED = 'PMM_SELECTED',
  PAYMENT_MADE = 'PAYMENT_MADE',
  PAYMENT_CONFIRMED = 'PAYMENT_CONFIRMED',
  SETTLEMENT_CONFIRMED = 'SETTLEMENT_CONFIRMED', // Final success state

  // Timeout states
  TRADE_TIMEOUT = 'TRADE_TIMEOUT',
  TIMEOUT = 'TIMEOUT',
  TIMEOUT_NO_DEPOSIT = 'TIMEOUT_NO_DEPOSIT',

  // Refund flow
  PRE_REFUND = 'PRE_REFUND',
  REFUNDED = 'REFUNDED', // Final refund state

  // Special states
  RENEW_QUOTE = 'RENEW_QUOTE',
  UNKNOWN = 'UNKNOWN',

  // Across trade flow
  BRIDGE_FAILED = 'BRIDGE_FAILED', // MPC bridge to destination chain failed
}

export const TRADE_SUCCESS_STATUS = [TradeStatus.PAYMENT_CONFIRMED, TradeStatus.SETTLEMENT_CONFIRMED]
export const ACROSS_TRADE_BTC_TO_EVM_SUCCESS_STATUS = [TradeStatus.SETTLEMENT_CONFIRMED]

export const TRADE_DEPOSIT_FAILED_STATUS = [TradeStatus.TIMEOUT_NO_DEPOSIT]

export const TRADE_DEPOSIT_SUCCESS_STATUS = [TradeStatus.DEPOSIT_CONFIRMED]

export const TRADE_FAILED_STATUS = [
  TradeStatus.TIMEOUT_NO_DEPOSIT,
  TradeStatus.PRE_REFUND,
  TradeStatus.REFUNDED,
  TradeStatus.TIMEOUT,
  TradeStatus.TRADE_TIMEOUT,
  TradeStatus.BRIDGE_FAILED,
]

export const ACROSS_BTC_TO_EVM_FAILED_STATUS = [
  TradeStatus.TIMEOUT,
  TradeStatus.TRADE_TIMEOUT,
  TradeStatus.BRIDGE_FAILED,
]

export const TRADE_PENDING_STATUS = [
  TradeStatus.INIT,
  TradeStatus.PRE_SUBMIT,
  TradeStatus.DEPOSITED,
  TradeStatus.DEPOSIT_CONFIRMED,
  TradeStatus.RENEW_QUOTE,
  TradeStatus.PMM_SELECTED,
  TradeStatus.PAYMENT_MADE,
]

export enum TradeFilterStatus {
  IN_PROCESS = 'IN_PROCESS',
  COMPLETED = 'COMPLETED',
  EXPIRED = 'EXPIRED',
  REFUNDED = 'REFUNDED',
  PRE_REFUND = 'PRE_REFUND',
}
// event status
export enum TradeAction {
  // Initial events
  INIT = 'INIT',
  PRE_SUBMIT = 'PRE_SUBMIT',
  // Across trade events
  SUBMITTED_TO_BE = 'SubmittedToBe',
  BRIDGED_TO = 'BridgedTo', // submitted bridge request
  MPC_BRIDGED_TO = 'MpcBridgedTo', // mpc submitted bridge: BTC -> EVM, or mpc bridged to refund to user (EVM -> BTC)
  FILLED_RELAY = 'FilledRelay', // User received token
  FILLED_RELAY_FAILED = 'FilledRelayFailed', // bridge failed

  // Deposit flow events
  DEPOSITED = 'Deposited',
  SUBMITTED_TO_SOLVER = 'SubmittedToSolver',

  // Main flow events
  SUBMIT_TRADE_INFO = 'SubmitTradeInfo',
  CONFIRM_DEPOSIT = 'ConfirmDeposit',
  // Fixed rate trade events
  CONFIRM_PRESIGN = 'ConfirmPresign',

  SELECT_PMM = 'SelectPMM',
  MAKE_PAYMENT = 'MakePayment',
  CONFIRM_PAYMENT = 'ConfirmPayment',
  CONFIRM_SETTLEMENT = 'ConfirmSettlement',

  // Refund flow events
  PRE_REFUND = 'PRE_REFUND',
  REFUND = 'Refund',
  REFUNDED = 'Refunded',
  CONFIRM_REFUND = 'ConfirmRefund',
  BRIDGE_REFUNDED = 'BridgeRefunded', // BTC -> EVM: Request refund success and  refunded on mainnet

  // Special event
  RENEW_QUOTE = 'RenewQuote',

  // Timeout states
  TRADE_TIMEOUT = 'TradeTimeout',
  TIMEOUT = 'Timeout',
  TIMEOUT_NO_DEPOSIT = 'TimeoutNoDeposit',

  REPORT_FAILURE = 'ReportFailure',

  UNKNOWN = 'UNKNOWN',
}

export const REFUND_ACTIONS = [
  TradeAction.PRE_REFUND,
  TradeAction.REFUND,
  TradeAction.REFUNDED,
  TradeAction.CONFIRM_REFUND,
  TradeAction.BRIDGE_REFUNDED, // For Across trade
]

export const COMPLETED_ACTIONS = [TradeAction.CONFIRM_SETTLEMENT, TradeAction.CONFIRM_PAYMENT]

export const PENDING_ACTIONS = [TradeAction.RENEW_QUOTE, TradeAction.REPORT_FAILURE, TradeAction.UNKNOWN]

export const TIMEOUT_ACTIONS = [TradeAction.TIMEOUT, TradeAction.TIMEOUT_NO_DEPOSIT]

export const DISABLED_ACTIONS = [...PENDING_ACTIONS, ...REFUND_ACTIONS, ...TIMEOUT_ACTIONS]

export const INTERNAL_TRANSACTION_ACTIONS = [TradeAction.INIT, TradeAction.PRE_SUBMIT, TradeAction.DEPOSITED]

export const SUPPORTED_ACTIONS = [
  TradeAction.INIT,
  TradeAction.PRE_SUBMIT,
  TradeAction.DEPOSITED,
  TradeAction.SUBMITTED_TO_SOLVER,

  TradeAction.SUBMIT_TRADE_INFO,
  TradeAction.CONFIRM_DEPOSIT,
  TradeAction.SELECT_PMM,
  TradeAction.MAKE_PAYMENT,
  TradeAction.CONFIRM_PAYMENT,
  TradeAction.CONFIRM_SETTLEMENT,

  TradeAction.PRE_REFUND,
  TradeAction.REFUND,
  TradeAction.REFUNDED,
  TradeAction.CONFIRM_REFUND,

  TradeAction.RENEW_QUOTE,

  ...TIMEOUT_ACTIONS,

  TradeAction.REPORT_FAILURE,
  TradeAction.UNKNOWN,
]

interface ITradeInfo {
  toChain: [string, string, string]
  amountIn: string
  fromChain?: [string, string, string]
  btcInfo?: [string, string, string]
}

interface IScriptInfo {
  depositInfo: string[]
  scriptTimeout: string
  userEphemeralL2Address: string
}

interface IPmmInfo {
  info: string[]
  amountOut: string
  sigExpiry: string
  selectedPmmId: string
}

interface IRfqInfo {
  minAmountOut: string
  tradeTimeout: number
  rfqInfoSignature: string
}

export interface ITradeInfoData {
  sessionId: string
  tradeInfo: ITradeInfo
  scriptInfo: IScriptInfo
}

interface IPresignInfo {
  pmmId: string
  presigns: string[]
  pmmRecvAddress?: string
  pmmReceivingAddress?: string
}

interface IInputData {
  to: string
  from: string
  data?: ITradeInfoData
  info?: {
    pmmInfo: IPmmInfo
    rfqInfo: IRfqInfo
  }
  pmmFinalists?: PmmProps[] // init and pre-submit
  solver?: string
  mpc?: string
  list?: string[]
  gasUsed: string
  tradeId: string
  signature?: string
  depositFromList?: string[]
  paymentTxId?: string
  pFeeRate?: string
  transactionHash: string
  presigns?: IPresignInfo[]
  refundPresign: RefundPresign
  releaseTxId?: string
  affiliateInfo?: AffiliateStruct

  amountIn?: string
  deadline?: number
  sessionId?: string

  txId?: string
  refundTxId?: string
  depositTxId?: string
  depositVault?: string
  minAmountOut?: string
  bestQuoteAfterFees?: string
  actualAmountOut?: string
  toUserAddress?: string
  fromUserAddress?: string
  sessionDeadline?: number

  userReceivingAddress?: string
  userEphemeralL2Address?: string
  fromToken?: IToken
  toToken?: IToken

  // rename
  // mpcPubkey?: string
  mpcAssetChainPubkey?: string
  // userEphemeralKey?: string
  userEphemeralAssetChainPubkey?: string
  // userRefundAddress?: string
  userRefundPubkey?: string
  best_quote?: string
  // across trade
  outputAmount?: string
  depositId?: string
  depositTxHash?: string
  depositRefundTxHash?: string
  // Submit across request refund
  details?: {
    recipient: string
    amount: string
    depositId: string
    depositTxHash: string
    depositRefundTxHash: string
    sessionId: string
  }
  outAmount?: string
  refundChainId?: number // Across trade refund chain id
  minAmountOutAfterBridge?: string // BTC -> EVM
  bestQuoteAfterFeesAfterBridge?: string // BTC -> EVM
  destinationChainId?: number // Across trade refund destination chain id
  fillTxHash?: string // Across trade refund fill tx hash
}

export interface IEvent {
  tradeId: string
  action: TradeAction
  txId: string
  blockNumber: number
  timestamp: number
  inputData: IInputData
  createdAt: string
  updatedAt: string
}

export enum TradeType {
  BASIC = 'BASIC',
  OPTIMISTIC = 'OPTIMISTIC',
}
export interface ITrade {
  id: number
  tradeId: string
  status: string
  timestamp: number
  tradeTimeout: number
  scriptTimeout: number
  fromUserAddress: string
  fromWalletAddress: string | null
  toUserAddress: string
  createdAt: string
  updatedAt: string
  events: IEvent[]
  swapType: TradeType
  fromToken: IToken | null
  toToken: IToken | null
  depositTxId?: string
  amountIn?: string
  settlementTxId?: string
  fromBridgeTxId?: string
  toBridgeTxId?: string
}

export interface ITradeRes {
  data: ITrade[]
  paging: Pagination
}

type IStepStatus = 'pending' | 'success' | 'default'

export type ITradeAction = TradeAction

export interface IStep {
  step: number
  explorerLink: string
  txHash: string
  status: IStepStatus
  actions: Partial<Record<ITradeAction, boolean>> & {
    [key: string]: boolean | undefined
  }
  timestamp: number
}

export interface ProcessedTrade extends ITrade {
  lastEvent: IEvent | null
  fromToken: IToken | null
  toToken: IToken | null
  minAmountOut: string
  amountOut: string
  amountIn: string
  tradeTimeout: number
}

export type TradeDetailFormated = {
  fromToken: IToken
  toToken: IToken
  amountIn: string
  amountInFormatted: string
  minAmountOut: string
  minAmountOutFormatted: string
  amountOut: string
  amountOutFormatted: string
  afterBridgeAmountOut: string
  afterBridgeAmountOutFormatted: string
  paymentTxId: string
  depositTxId: string
  status: TradeStatus
  tradeStatus: 'PENDING' | 'SUCCESS' | 'FAILED'
  hasSelectPMM: boolean
  completedTime: number
  scriptTimeout: string | number
  bestQuoteAfterFees: string | undefined
  actualAmountOut: string | undefined
  depositVault: string
  toUserAddress: string
  fromUserAddress: string
  refundTxId: string
  depositedInputData: InputDataPresubmitTrade
  swapType: TradeType
  isExternalTransaction?: boolean
  amountInUnit: string
  amountOutUnit: string
  minAmountOutUnit: string
  originRate: number
  fromNetwork?: 'BTC' | 'EVM' | 'SOL'
  isConfirmDeposit?: boolean
  hasRenewQuote?: boolean
  lastEvent: IEvent | null
  tradeTimeout: number
  isRefunded?: boolean
  tradePairInfo?: TradePairInfo
  events: IEvent[]
  fromBridgeTxId: string
  toBridgeTxId: string
  acrossTradeToUserAddress: string
  affiliateInfo?: AffiliateStruct
  sessionId: string
  acrossRefundToUserAddress: string
  isAcrossBridgeTrade?: boolean
  isOptimexTradeCompleted?: boolean
  refundChainId?: number
  minAmountOutAfterBridge?: string
  minAmountOutAfterBridgeUnit?: string
  bestQuoteAfterFeesAfterBridge?: string
  //TODO
  amountOutBeforeBridge?: string
}
